import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useForm, Controller } from 'react-hook-form';

import {
  useFetchArbolCierre,
  useCreateCierreCategoria,
  useDeleteCierreCategoria,
  useCreateCierreSubcategoria,
  useDeleteCierreSubcategoria,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-catalogos.actions';
import { ITicketArea } from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';

interface PanelArbolCierreProps {
  areasEditables: ITicketArea[];
  areaFilter?: string | null;
  isSuperUser: boolean;
}

interface NewCategoriaForm {
  nombre: string;
  codigo: string;
  area_id: string;
}

interface NewSubcategoriaForm {
  nombre: string;
  codigo: string;
  cierre_categoria: string;
}

/**
 * Panel del arbol de cierre (2 niveles: Categoria -> Subcategoria) por
 * area. Reemplaza al panel viejo de motivos planos.
 *
 * Visualizacion:
 *  - Lista de categorias del area (chip con (default) si son genericas).
 *  - Subcategorias colgando debajo de cada una.
 *  - Formularios inline para agregar categoria / subcategoria.
 */
export default function PanelArbolCierre({
  areasEditables,
  areaFilter,
  isSuperUser,
}: PanelArbolCierreProps) {
  // Área seleccionada para administrar/visualizar. Arranca con el filtro
  // global de la página (área del supervisor / lo elegido en el tab Tipos)
  // pero se puede cambiar aquí mismo: antes este tab no tenía selector y el
  // admin veía todas las categorías mezcladas sin diferenciar por área.
  const [areaSel, setAreaSel] = useState<string>(areaFilter || '');
  useEffect(() => {
    setAreaSel(areaFilter || '');
  }, [areaFilter]);

  const { data: arbolRes, isLoading } = useFetchArbolCierre(areaSel || null);
  const arbolRaw: any[] = (arbolRes as any)?.data ?? [];
  // Orden estable para la vista "Todas las áreas": por área (genéricas al
  // final) y luego por nombre — cada área queda como un bloque contiguo.
  const arbol: any[] = [...arbolRaw].sort((a: any, b: any) => {
    const ga = a.es_generica ? 1 : 0;
    const gb = b.es_generica ? 1 : 0;
    if (ga !== gb) return ga - gb;
    const porArea = String(a.area_nombre || '').localeCompare(String(b.area_nombre || ''));
    if (porArea !== 0) return porArea;
    return String(a.nombre || '').localeCompare(String(b.nombre || ''));
  });

  const createCategoria = useCreateCierreCategoria();
  const deleteCategoria = useDeleteCierreCategoria();
  const createSubcategoria = useCreateCierreSubcategoria();
  const deleteSubcategoria = useDeleteCierreSubcategoria();

  const [openNewCat, setOpenNewCat] = useState(false);
  const [openNewSub, setOpenNewSub] = useState<{ categoriaId: string; categoriaNombre: string } | null>(null);
  const [toDelete, setToDelete] = useState<
    | { tipo: 'categoria'; id: string; nombre: string }
    | { tipo: 'subcategoria'; id: string; nombre: string }
    | null
  >(null);

  // ---- form Nueva Categoria ----
  const catForm = useForm<NewCategoriaForm>({
    defaultValues: { nombre: '', codigo: '', area_id: areaSel || '' },
  });
  const onSubmitCat = (data: NewCategoriaForm) => {
    createCategoria.mutate(
      {
        codigo: data.codigo,
        nombre: data.nombre,
        area: data.area_id || null,
        activa: true,
      },
      {
        onSuccess: () => {
          catForm.reset({ nombre: '', codigo: '', area_id: areaFilter || '' });
          setOpenNewCat(false);
        },
      }
    );
  };

  // ---- form Nueva Subcategoria ----
  const subForm = useForm<NewSubcategoriaForm>({
    defaultValues: { nombre: '', codigo: '', cierre_categoria: '' },
  });
  const onSubmitSub = (data: NewSubcategoriaForm) => {
    createSubcategoria.mutate(
      {
        codigo: data.codigo,
        nombre: data.nombre,
        cierre_categoria: data.cierre_categoria,
        activa: true,
      },
      {
        onSuccess: () => {
          subForm.reset({ nombre: '', codigo: '', cierre_categoria: '' });
          setOpenNewSub(null);
        },
      }
    );
  };

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    if (toDelete.tipo === 'categoria') {
      deleteCategoria.mutate(toDelete.id, { onSuccess: () => setToDelete(null) });
    } else {
      deleteSubcategoria.mutate(toDelete.id, { onSuccess: () => setToDelete(null) });
    }
  };

  const areaPreSeleccionadaBloqueada = !isSuperUser && !!areaFilter;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Árbol de Cierre</Typography>
          <Typography variant="caption" color="text.secondary">
            {isSuperUser
              ? 'Admin: Categorías + Subcategorías que se muestran al cerrar tickets. "(default)" son genéricas para áreas que no tienen propias.'
              : 'Define la jerarquía de motivos al cerrar tickets de tu área. Si tu área no tiene propios, aparecerán los genéricos (default).'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            catForm.reset({ nombre: '', codigo: '', area_id: areaSel || (areasEditables[0]?.id_area ?? '') });
            setOpenNewCat(true);
          }}
        >
          Nueva categoría
        </Button>
      </Stack>

      {/* Selector de área: la vista es POR ÁREA — lo mismo que verá el modal
          al cerrar un ticket de esa área (propias + "Aplica para cierre" del
          árbol de creación, o defaults si no tiene nada propio). */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Área"
          value={areaSel}
          onChange={(e) => setAreaSel(e.target.value)}
          sx={{ minWidth: 280 }}
          disabled={areaPreSeleccionadaBloqueada || (!isSuperUser && areasEditables.length <= 1)}
          helperText={
            areaSel
              ? 'Mostrando exactamente lo que verá el modal al cerrar tickets de esta área'
              : 'Todas las áreas, agrupadas por área (defaults al final)'
          }
        >
          {isSuperUser && <MenuItem value="">Todas las áreas</MenuItem>}
          {areasEditables.map((a: any) => (
            <MenuItem key={a.id_area} value={a.id_area}>
              {a.nombre}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : arbol.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No hay categorías de cierre. Crea la primera con el botón de arriba.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {arbol.map((cat: any, idx: number) => {
            // Si la categoria es default (generica, area=NULL), solo admin
            // global puede tocarla. Esto evita el bug original donde un
            // supervisor borraba lo default y desaparecia de TODAS las areas.
            // Las de origen_arbol ("Aplica para cierre" del arbol de
            // creacion) se administran en el tab Categorias: aqui solo se ven.
            const esEditable = !cat.origen_arbol && (!cat.es_generica || isSuperUser);
            // En "Todas las areas" se intercala un encabezado cada vez que
            // cambia el area (genericas al final como "Default").
            const grupo = cat.es_generica
              ? 'Default (todas las áreas)'
              : cat.area_nombre || 'Tipos legacy (todas las áreas)';
            const grupoPrev =
              idx === 0
                ? null
                : arbol[idx - 1].es_generica
                ? 'Default (todas las áreas)'
                : arbol[idx - 1].area_nombre || 'Tipos legacy (todas las áreas)';
            const muestraGrupo = !areaSel && grupo !== grupoPrev;
            return (
            <Box key={cat.id_cierre_categoria}>
              {muestraGrupo && (
                <Typography
                  variant="overline"
                  color="primary"
                  sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}
                >
                  {grupo}
                </Typography>
              )}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {cat.nombre}
                </Typography>
                {cat.es_generica ? (
                  <Tooltip title="Categoría compartida entre áreas. Solo admin global puede editarla.">
                    <Chip label="default" size="small" color="default" />
                  </Tooltip>
                ) : (
                  <>
                    <Chip label={cat.area_nombre || 'Todas las áreas'} size="small" color="primary" variant="outlined" />
                    {cat.origen_arbol && (
                      <Tooltip title={'Categoría del árbol de creación marcada "Aplica para cierre". Se administra en el tab Árbol de Creación; si su tipo no tiene área (legacy), aplica a todas las áreas con "Legacy Apertura" activo. Mientras un área tenga al menos una, deja de ver las default.'}>
                        <Chip label="Aplica para cierre" size="small" color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                  </>
                )}
                <Box sx={{ flex: 1 }} />
                {esEditable && (
                  <Tooltip title="Agregar subcategoría">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => {
                        subForm.reset({ nombre: '', codigo: '', cierre_categoria: cat.id_cierre_categoria });
                        setOpenNewSub({ categoriaId: cat.id_cierre_categoria, categoriaNombre: cat.nombre });
                      }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {esEditable && (
                  <Tooltip title="Eliminar categoría">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setToDelete({ tipo: 'categoria', id: cat.id_cierre_categoria, nombre: cat.nombre })
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
              {cat.subcategorias.length === 0 ? (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                  Sin subcategorías. Agrega la primera con el botón "+".
                </Typography>
              ) : (
                <Stack spacing={0.75} sx={{ pl: 2 }}>
                  {cat.subcategorias.map((sub: any) => (
                    <Stack
                      key={sub.id_cierre_subcategoria}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ py: 0.5, borderBottom: '1px dashed', borderColor: 'divider' }}
                    >
                      <Typography variant="body2">{sub.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({sub.codigo})
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {esEditable && (
                        <Tooltip title="Eliminar subcategoría">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setToDelete({
                                tipo: 'subcategoria',
                                id: sub.id_cierre_subcategoria,
                                nombre: sub.nombre,
                              })
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Paper>
            </Box>
            );
          })}
        </Stack>
      )}

      {/* Dialog nueva categoria */}
      <Dialog open={openNewCat} onClose={() => setOpenNewCat(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva categoría de cierre</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              {...catForm.register('nombre', { required: 'El nombre es requerido' })}
              label="Nombre"
              fullWidth size="small"
              error={!!catForm.formState.errors.nombre}
              helperText={catForm.formState.errors.nombre?.message}
            />
            <TextField
              {...catForm.register('codigo', { required: 'El código es requerido' })}
              label="Código"
              fullWidth size="small"
              error={!!catForm.formState.errors.codigo}
              helperText={catForm.formState.errors.codigo?.message || 'Identificador único'}
            />
            <Controller
              name="area_id"
              control={catForm.control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Área"
                  size="small"
                  fullWidth
                  disabled={areaPreSeleccionadaBloqueada}
                  helperText={
                    areaPreSeleccionadaBloqueada
                      ? 'Atado a tu área'
                      : isSuperUser
                      ? 'Sin área = default global (compartida con áreas que aún no tienen propias)'
                      : 'Tu área'
                  }
                >
                  {isSuperUser && <MenuItem value="">Sin área (default global)</MenuItem>}
                  {areasEditables.map((a: any) => (
                    <MenuItem key={a.id_area} value={a.id_area}>{a.nombre}</MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewCat(false)} color="inherit">Cancelar</Button>
          <Button
            onClick={catForm.handleSubmit(onSubmitCat)}
            variant="contained"
            disabled={createCategoria.isPending}
          >
            {createCategoria.isPending ? '...' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog nueva subcategoria */}
      <Dialog open={!!openNewSub} onClose={() => setOpenNewSub(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Nueva subcategoría bajo "{openNewSub?.categoriaNombre}"
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              {...subForm.register('nombre', { required: 'El nombre es requerido' })}
              label="Nombre"
              fullWidth size="small"
              error={!!subForm.formState.errors.nombre}
              helperText={subForm.formState.errors.nombre?.message}
            />
            <TextField
              {...subForm.register('codigo', { required: 'El código es requerido' })}
              label="Código"
              fullWidth size="small"
              error={!!subForm.formState.errors.codigo}
              helperText={subForm.formState.errors.codigo?.message}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewSub(null)} color="inherit">Cancelar</Button>
          <Button
            onClick={subForm.handleSubmit(onSubmitSub)}
            variant="contained"
            disabled={createSubcategoria.isPending}
          >
            {createSubcategoria.isPending ? '...' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminar */}
      <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
        <DialogTitle>Eliminar {toDelete?.tipo === 'categoria' ? 'categoría' : 'subcategoría'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Eliminar <strong>{toDelete?.nombre}</strong>?
            {toDelete?.tipo === 'categoria' && ' Sus subcategorías también dejarán de aparecer.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} color="inherit">Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ mt: 3 }} />
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        ℹ Si un área no tiene categorías/subcategorías propias, al cerrar un ticket se muestran las
        marcadas como "(default)" — el mismo patrón que el toggle "Incluir Legacy" del árbol de creación.
        Las categorías del árbol de creación marcadas "Aplica para cierre" también aparecen al cerrar
        tickets de su área — y mientras exista al menos una, esa área deja de ver las default.
        Si el tipo de la categoría no tiene área (legacy), aplica a todas las áreas con "Legacy Apertura" activo.
      </Typography>
    </Box>
  );
}
